import { useState, useMemo } from "react";
import { useProducts } from "@/hooks/useProducts";
import { usePriceBooks } from "@/hooks/usePriceBooks";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Search, DollarSign } from "lucide-react";

export function PricesGrid() {
  const { products, isLoading: productsLoading } = useProducts();
  const { priceBooks, productPrices, isLoading: pricesLoading, updatePrice } = usePriceBooks();
  const [search, setSearch] = useState("");
  const [categoryFilter, setCategoryFilter] = useState<string>("all");
  const [editingCell, setEditingCell] = useState<string | null>(null);
  const [editValue, setEditValue] = useState("");

  const categories = useMemo(() => {
    const cats = new Set(
      products
        ?.filter(p => p.category !== 'Shingle Colors')
        .map(p => p.category) || []
    );
    return Array.from(cats).sort();
  }, [products]);

  const filteredProducts = products?.filter((product) => {
    const matchesSearch = 
      product.name.toLowerCase().includes(search.toLowerCase()) ||
      product.sku.toLowerCase().includes(search.toLowerCase());
    const matchesCategory = 
      categoryFilter === "all" || product.category === categoryFilter;
    const isNotColorSelection = product.category !== 'Shingle Colors';
    return matchesSearch && matchesCategory && product.is_active && isNotColorSelection;
  });

  const getPriceForProduct = (productId: string, priceBookId: string) => {
    return productPrices?.find(
      (pp) => pp.product_id === productId && pp.price_book_id === priceBookId
    );
  };

  const getEffectiveUnit = (product: any) => {
    const chargeMethod = product.calculation_config?.charge_method;
    if (chargeMethod === 'sqft') return 'sq ft';
    if (chargeMethod === 'lf') return 'lf';
    return product.unit;
  };

  const handleCellClick = (productId: string, priceBookId: string) => {
    const cellKey = `${productId}-${priceBookId}`;
    const currentPrice = getPriceForProduct(productId, priceBookId);
    setEditingCell(cellKey);
    setEditValue(currentPrice?.unit_price?.toString() || "");
  };

  const handleCellBlur = async (productId: string, priceBookId: string) => {
    const value = parseFloat(editValue);
    if (!isNaN(value) && value >= 0) {
      await updatePrice.mutateAsync({
        product_id: productId,
        price_book_id: priceBookId,
        unit_price: value,
      });
    }
    setEditingCell(null);
  };

  const handleKeyDown = (e: React.KeyboardEvent, productId: string, priceBookId: string) => {
    if (e.key === "Enter") {
      handleCellBlur(productId, priceBookId);
    } else if (e.key === "Escape") {
      setEditingCell(null);
    }
  };

  const isLoading = productsLoading || pricesLoading;

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle>Price Matrix</CardTitle>
          <div className="flex gap-2">
            <Button variant="outline" size="sm">
              <DollarSign className="mr-2 h-4 w-4" />
              Bulk Adjust
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          <div className="flex gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search products..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9"
              />
            </div>
            <Select value={categoryFilter} onValueChange={setCategoryFilter}>
              <SelectTrigger className="w-[200px]">
                <SelectValue placeholder="All Categories" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                {categories.map((cat) => (
                  <SelectItem key={cat} value={cat}>
                    {cat}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="rounded-md border overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[100px]">SKU</TableHead>
                  <TableHead className="min-w-[200px]">Product</TableHead>
                  <TableHead className="w-[80px]">Unit</TableHead>
                  {priceBooks?.map((pb) => (
                    <TableHead key={pb.id} className="text-center w-[120px]">
                      {pb.county}
                    </TableHead>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading ? (
                  <TableRow>
                    <TableCell colSpan={3 + (priceBooks?.length || 0)} className="text-center">
                      Loading prices...
                    </TableCell>
                  </TableRow>
                ) : filteredProducts?.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={3 + (priceBooks?.length || 0)} className="text-center">
                      No products found
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredProducts?.map((product) => (
                    <TableRow key={product.id}>
                      <TableCell className="font-mono text-sm">{product.sku}</TableCell>
                      <TableCell className="font-medium">{product.name}</TableCell>
                      <TableCell className="capitalize text-sm">
                        {getEffectiveUnit(product)}
                      </TableCell>
                      {priceBooks?.map((pb) => {
                        const cellKey = `${product.id}-${pb.id}`;
                        const price = getPriceForProduct(product.id, pb.id);
                        const isEditing = editingCell === cellKey;
                        const effectiveUnit = getEffectiveUnit(product);

                        return (
                          <TableCell
                            key={pb.id}
                            className="text-center cursor-pointer hover:bg-muted/50"
                            onClick={() => !isEditing && handleCellClick(product.id, pb.id)}
                          >
                            {isEditing ? (
                              <Input
                                type="number"
                                step="0.01"
                                min="0"
                                value={editValue}
                                onChange={(e) => setEditValue(e.target.value)}
                                onBlur={() => handleCellBlur(product.id, pb.id)}
                                onKeyDown={(e) => handleKeyDown(e, product.id, pb.id)}
                                autoFocus
                                className="h-8 text-center"
                              />
                            ) : (
                              <span className={price ? "text-foreground" : "text-muted-foreground"}>
                                {price ? (
                                  <>
                                    ${price.unit_price.toFixed(2)}
                                    <span className="text-xs text-muted-foreground ml-1">/ {effectiveUnit}</span>
                                  </>
                                ) : "—"}
                              </span>
                            )}
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>

          <div className="text-sm text-muted-foreground space-y-2">
            <p>
              Click on any price cell to edit. Press Enter to save or Escape to cancel.
            </p>
            <p className="text-xs">
              💡 The <strong>Unit</strong> column shows what the price represents, based on each product's <strong>Charging Method</strong> configuration:
            </p>
            <ul className="text-xs list-disc list-inside pl-2 space-y-1">
              <li><strong>sq ft</strong> = Price is per square foot of coverage</li>
              <li><strong>lf</strong> = Price is per linear foot</li>
              <li><strong>roll/bundle/each</strong> = Price is per unit of product</li>
            </ul>
            <p className="text-xs italic">
              Note: Shingle Colors are display-only selections and don't require pricing.
            </p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
